import { DateTime } from 'luxon';
import { UserConsentId, UserConsentRecords } from './user-consent-interface';

/**
 * When an account has to answer the consent screen again (#5587).
 *
 * The screen used to be asked once, at sign-up, and never again: the app showed it
 * only while the account held no Terms record at all. So when the Terms or the
 * Privacy Policy change, every existing account carries an answer to text nobody
 * reads any more — and the people who were never asked about Advertising, because
 * the question did not exist when they signed up, never get asked at all.
 *
 * The owner's decision (2026-09-18) is to ask everybody again when the text
 * changes, and to do it gently first: for a month the app only shows a card on the
 * home dashboard, and the screen can be closed without answering. After that month
 * the screen blocks the app the way it does for a new sign-up, so the stragglers
 * are caught rather than left behind for ever.
 */

/**
 * The version of the Terms / Privacy text that needs a fresh answer (#850).
 *
 * **Raise this by one whenever the Terms or the Privacy Policy change in a way
 * everybody must accept again.** It is the one switch that re-asks the whole user
 * base. The app sends it with every answer, the server stores it on the records,
 * and an account whose Terms record carries a lower number, or none, is asked
 * again.
 *
 * The CI gate `scripts/ci/check-policy-text-version.mjs` refuses a push that
 * changes the policy pages without deciding about this number: raise it, or
 * record in so many words why the edit is too small to ask everybody again.
 *
 * Version 1 is every text before #5586 ("Name Meta and Google Ads as recipients of
 * web purchase data in the privacy policy"); no record was ever stamped with it.
 *
 * Version 3 (#5814) is the Terms screen's own wording, not the policy pages: its
 * Advertising box used to ask only to measure which ads work, and now also asks to
 * let the ads reach the right people. The server already tells Google Ads that a
 * yes allows ad personalisation, so an answer to the narrower text did not cover
 * what the code does. Version 2 only ever reached canary.
 *
 * Why a number and not a date: this used to be the date the new text went live.
 * A date has to be set on release day exactly — set it early, and everybody who
 * accepts on the old app between that date and the release counts as having
 * accepted the new text and is never asked; set it late, and people who accept in
 * between are asked again until the date passes. A number is stamped by the app
 * that actually showed the text, so it is right whenever the release happens.
 */
export const CONSENT_TEXT_VERSION = 3;

/** How long the gentle phase lasts before the screen starts blocking the app,
 *  counted from when this account was first asked (see `refreshAsked`). */
export const CONSENT_REFRESH_GRACE_DAYS = 30;

export enum ConsentRefresh {
  /** the account's answer is current: show nothing */
  None = 'none',
  /** ask, but do not block — a home-dashboard card, and a screen that can be closed */
  Ask = 'ask',
  /** the screen blocks the app until it is answered, as it does for a new sign-up */
  Require = 'require',
}

/**
 * Does this account hold a Terms record at all?
 *
 * The presence of the record is what the app has always meant by "accepted", and
 * this keeps to it rather than reading `accepted` — the server refuses the whole
 * save when a mandatory consent is not given, so a ToS record that says no cannot
 * be written in the first place.
 */
export function hasTermsRecord(consents: UserConsentRecords | undefined | null): boolean {
  const record: unknown = consents?.[UserConsentId.ToS];
  return typeof record === 'object' && record !== null;
}

/** A text version as stored: a whole number of at least 1. Anything else — a
 *  missing field, or a damaged one — is not a version. */
export function isConsentTextVersion(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1;
}

/** The Terms record's fields this file reads, each one still unchecked: the record
 *  may have been written by any older app, or be damaged. */
function termsRecord(consents: UserConsentRecords): { textVersion?: unknown; refreshAsked?: unknown } {
  return consents[UserConsentId.ToS] as { textVersion?: unknown; refreshAsked?: unknown };
}

/**
 * When this account was first asked to accept `version`. Undefined when it has not
 * been, or when the mark belongs to another version or cannot be read.
 */
export function refreshAskedAt(
  consents: UserConsentRecords | undefined | null,
  version: number = CONSENT_TEXT_VERSION,
): DateTime | undefined {
  if (!hasTermsRecord(consents)) return undefined;
  const mark = termsRecord(consents).refreshAsked;
  if (typeof mark !== 'object' || mark === null) return undefined;
  const { textVersion, at_utc } = mark as { textVersion?: unknown; at_utc?: unknown };
  if (textVersion !== version || typeof at_utc !== 'string') return undefined;
  const at = DateTime.fromISO(at_utc, { zone: 'utc' });
  return at.isValid ? at : undefined;
}

/**
 * What this account has to be shown right now.
 *
 * - It holds no Terms record — a new account, or one that abandoned the screen:
 *   `Require`, which is exactly what the app did before this existed.
 * - Its Terms record carries the current text version or a newer one: `None`.
 * - It accepted an older version, or a record with no version at all: `Ask`,
 *   until `CONSENT_REFRESH_GRACE_DAYS` have passed since the account was first
 *   asked, and `Require` after that. An account nobody has marked as asked yet is
 *   always `Ask` — the month cannot have run out before it started.
 *
 * A version that cannot be read is treated as older. It cannot be shown to be
 * current, and the cost of asking again is one screen, while the cost of not
 * asking is an answer to text the person never saw.
 */
export function decideConsentRefresh(
  consents: UserConsentRecords | undefined | null,
  now: DateTime,
  currentVersion: number = CONSENT_TEXT_VERSION,
): ConsentRefresh {
  if (!hasTermsRecord(consents)) return ConsentRefresh.Require;

  // an unreadable current version can only come from a bad edit of the constant
  // above; asking the whole user base because of a typo is the worse failure, so
  // this answers "nothing to do" and leaves new sign-ups alone with the check above
  if (!isConsentTextVersion(currentVersion)) return ConsentRefresh.None;

  const accepted = termsRecord(consents).textVersion;
  if (isConsentTextVersion(accepted) && accepted >= currentVersion) return ConsentRefresh.None;

  const askedAt = refreshAskedAt(consents, currentVersion);
  if (!askedAt) return ConsentRefresh.Ask;
  const blocksFrom = askedAt.plus({ days: CONSENT_REFRESH_GRACE_DAYS });
  return now.toMillis() >= blocksFrom.toMillis() ? ConsentRefresh.Require : ConsentRefresh.Ask;
}

/**
 * True when the account is being asked about the current text and nothing has yet
 * recorded when that started — the app should record it now, so the month starts.
 */
export function consentRefreshNeedsAskedMark(
  consents: UserConsentRecords | undefined | null,
  now: DateTime,
  currentVersion: number = CONSENT_TEXT_VERSION,
): boolean {
  return decideConsentRefresh(consents, now, currentVersion) === ConsentRefresh.Ask
    && !refreshAskedAt(consents, currentVersion);
}

/** True while the consent screen must not be closable and must block the app. */
export function consentRefreshBlocksApp(refresh: ConsentRefresh): boolean {
  return refresh === ConsentRefresh.Require;
}
